import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import type { WikiTopic } from "../types.js";

export const factorsAndDeciles: WikiTopic = {
  slug: "factors-and-deciles",
  title: "Factors, percentiles, and deciles",
  summary:
    "A percentile is a rank on the raw number, and only the composite corrects for which direction is good.",
  group: "concepts",
  sections: [
    {
      heading: "From a raw value to a decile",
      code: "raw_value / percentile / decile",
      blocks: [
        p(
          <>
            A factor computes one raw number per symbol across the ranked universe. That column
            becomes a cross-sectional percentile: <Datum>0</Datum> for the lowest raw value,{" "}
            <Datum>1</Datum> for the highest, ties averaged. The rank is taken on the untransformed
            raws and before any clipping, because ranks survive a monotone transform anyway and
            winsorising first would manufacture ties at both tails.
          </>,
        ),
        p(
          <>
            The decile is the percentile bucketed: <Datum>floor(percentile × 10) + 1</Datum>, capped
            at <Datum>10</Datum>. The cap is what keeps the single highest-ranked name, whose
            percentile is exactly <Datum>1</Datum>, in decile <Datum>10</Datum> instead of an
            eleventh that does not exist.
          </>,
        ),
        note(
          <>
            A missing input stays null the whole way through. A factor that turned an absent
            fundamental into a zero would rank that company at the population mean, which is a
            confident statement about a company you know nothing about.
          </>,
        ),
      ],
    },
    {
      heading: "A percentile is not direction-corrected",
      blocks: [
        p(
          <>
            This is the number most likely to be misread on the screener. A percentile says where
            the raw value sits in the cross-section and nothing else. It does not know whether a big
            number is good.
          </>,
        ),
        p(
          <>
            Three of the eleven factors are <Datum>lower_is_better</Datum>, so on those three a high
            rank is the bad end. A <Datum>vol_60d</Datum> percentile of <Datum>95</Datum> means the
            name is among the most volatile in the market. A high <Datum>net_debt_to_ebitda</Datum>{" "}
            rank means more leverage, and a high <Datum>accrual_ratio</Datum> rank means more of the
            profit is accrual rather than cash.
          </>,
        ),
        table(
          "The eleven factors and which direction is good",
          ["Factor", "Good direction"],
          [
            ["mom_12m_1m", "Higher"],
            ["mom_6m", "Higher"],
            ["vol_60d", "Lower"],
            ["turnover_60d", "Higher"],
            ["delivery_spike", "Higher"],
            ["high_52w_proximity", "Higher"],
            ["roce_ttm", "Higher"],
            ["net_debt_to_ebitda", "Lower"],
            ["accrual_ratio", "Lower"],
            ["fcf_yield", "Higher"],
            ["earnings_yield", "Higher"],
          ],
        ),
        note(
          <>
            The screener's rank cell colours the top decile up and the bottom decile down on every
            column alike, because the colour follows the rank. On the three lower-is-better factors,
            read that colour as "extreme", not as "good".
          </>,
        ),
      ],
    },
    {
      heading: "The composite is the number that flips",
      code: "composite",
      blocks: [
        p(
          <>
            The composite is the equal-weighted mean of sign-corrected z-scores. A{" "}
            <Datum>lower_is_better</Datum> factor's z is multiplied by <Datum>-1</Datum> before
            averaging, so a higher composite always means a better name. It averages the neutralised
            z, which is measured inside the symbol's own sector wherever that sector holds enough
            spread-out values to stand as a peer set.
          </>,
        ),
        p(<>Three rules keep the average honest.</>),
        ul([
          <>
            Only factors that actually ran are passed in, so an unavailable factor is absent from
            the weight set rather than sitting in it as a zero.
          </>,
          <>
            A symbol missing one contributor is compared on the rest, with the weights renormalised
            for that symbol alone.
          </>,
          <>
            Below <Datum>4</Datum> contributing factors, which is{" "}
            <Datum>DEFAULT_COMPOSITE.minConstituents</Datum>, the composite is null for that symbol.
            A two-factor mean wearing a six-factor name looks normal and measures something else.
          </>,
          <>
            <Datum>turnover_60d</Datum> is a gate, not a term. It carries no weight in the mean, and
            a name whose 60-day median turnover is under <Datum>Rs 5 Cr</Datum> gets no composite at
            all. Ranked as a factor, higher turnover lost about 3% a year against the liquid
            universe. The column and its percentile stay on the screen.
          </>,
        ]),
        p(
          <>
            The composite column carries a percentile of its own, and that one is safe to read as
            merit: the z it ranks was already sign-corrected. It is the only percentile on the
            screen for which "higher is better" holds without checking the factor.
          </>,
        ),
      ],
    },
    {
      heading: "What the ranking cannot see",
      code: "basis = 'consolidated'",
      blocks: [
        p(
          <>
            The scoring pipeline loads fundamentals with <Datum>basis = 'consolidated'</Datum>. A
            company that files standalone statements only has real numbers on file that the pipeline
            never reads, so it carries no fundamental factor and no composite. It is not ranked
            badly; it is not ranked.
          </>,
        ),
        p(
          <>
            The filter is right for what it protects. Standalone and consolidated are two different
            true statements about the same company, and a trailing twelve months mixing them is
            neither. So a TTM takes four quarters or nothing, all on one basis. The cost is the
            standalone-only filers, and a standalone fallback is the recorded follow-up.
          </>,
        ),
        note(
          <>
            The same filter is why <Datum>315</Datum> of the symbols in the feed have no market cap
            on file. Measured on prod on <Datum>2026-09-05</Datum>.
          </>,
        ),
      ],
    },
  ],
};
